import { useCallback, useRef } from "react";
import useSerialQueue from "./useSerialQueue";

export type StepContextUpdate =
  | { context: "replace"; replacement: string }
  | { context: "reset" }
  | { context: "preserve" }
  // The chain is closed: the further steps fail without being run.
  | { context: "closed" };

export type StepFn<Response> = (
  requestBlob: string,
  context?: string,
) => Promise<Response>;

export type StepWithContextFn<Response> = (
  requestBlob: string,
) => Promise<Response>;

// The step was not run, as the chain is already closed.
export class StepChainClosedError extends Error {
  constructor() {
    super("The chain of steps is already closed");

    // Keep `instanceof` working when compiled to ES5.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export type ExtractContextFn<Response> = (
  response: Response,
) => StepContextUpdate;

// Track the context across the steps of a flow: pass the context from the
// previous step to the next one, and start without it.
//
// The steps form a single chain, so they are run one after another: a step
// only starts once the context from the previous one is applied.
const useStepContext = <Response>(
  step: StepFn<Response>,
  extractContext: ExtractContextFn<Response>,
): [stepWithContext: StepWithContextFn<Response>, reset: () => void] => {
  const contextRef = useRef<string>();
  const closedRef = useRef(false);

  const enqueue = useSerialQueue();

  const stepWithContext = useCallback(
    (requestBlob: string) =>
      enqueue(async () => {
        if (closedRef.current) throw new StepChainClosedError();

        const res = await step(requestBlob, contextRef.current);

        const update = extractContext(res);
        switch (update.context) {
          case "replace":
            contextRef.current = update.replacement;
            break;
          case "reset":
            contextRef.current = undefined;
            break;
          case "preserve":
            break;
          case "closed":
            closedRef.current = true;
            break;
        }

        return res;
      }),
    [enqueue, step, extractContext],
  );

  const reset = useCallback(() => {
    contextRef.current = undefined;
    closedRef.current = false;
  }, []);

  return [stepWithContext, reset];
};

export default useStepContext;
